import {
    AlertTriangle,
    BookOpen,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import { Fragment } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { EmptyState } from '@apps/web/components/ui/empty_state';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { DEFAULT_PER_PAGE } from '@apps/web/features/admin_agent_scratchpad/model/constants';
import { type ScratchpadSearch } from '@apps/web/features/admin_agent_scratchpad/model/search_params';
import { useScratchpadIndex } from '@apps/web/features/admin_agent_scratchpad/model/use_scratchpad_index';
import { ScratchpadFilterBar } from '@apps/web/features/admin_agent_scratchpad/ui/filter_bar';
import { ScratchpadNoteRow } from '@apps/web/features/admin_agent_scratchpad/ui/note_row';

/**
 * `/admin/agent/scratchpad` — vault-style index for durable admin notes.
 * Reads-only. Agent is the write channel; humans browse, filter, audit.
 */
export function ScratchpadIndexPage({
    search,
}: {
    readonly search: ScratchpadSearch;
}) {
    const {
        data,
        isLoading,
        hasError,
        selectionIsValid,
        selectedScopeTypes,
        searchDraft,
        setSearchDraft,
        clearSearchDraft,
        patchSearch,
    } = useScratchpadIndex(search);
    // Highlight the scope kind the user still needs to pin down. When
    // several non-global kinds are queued, name the first — the empty state
    // is a nudge, not an exhaustive description of the selection.
    const promptForScopeKind =
        selectedScopeTypes.find((kind) => kind !== 'global') ??
        selectedScopeTypes[0] ??
        null;
    const hasInvalidScopeId =
        search.scope_id !== undefined &&
        (selectedScopeTypes.length === 0 ||
            selectedScopeTypes.includes('global'));
    const hasNoScopeKindForId =
        hasInvalidScopeId && selectedScopeTypes.length === 0;

    const notes = data?.notes ?? [];
    const availableTags = data?.available_tags ?? [];
    const totalPages = data
        ? Math.max(1, Math.ceil(data.total / DEFAULT_PER_PAGE))
        : 1;
    const showDegradedBanner = data?.degraded_search === true;

    return (
        <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-4 px-6 pt-6 pb-10 lg:px-10">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex items-start gap-3">
                    <span
                        className="border-border/40 bg-muted/40 text-foreground/80 mt-0.5 hidden h-9 w-9 items-center justify-center rounded-md border sm:inline-flex"
                        aria-hidden="true"
                    >
                        <BookOpen className="h-4 w-4" />
                    </span>
                    <div>
                        <h1 className="text-xl font-semibold tracking-tight">
                            Agent scratchpad
                        </h1>
                        <p className="text-muted-foreground max-w-prose text-sm">
                            Durable, wiki-style memory the admin agent writes
                            across runs. Search first, trust stable notes,
                            verify volatile ones.
                        </p>
                    </div>
                </div>
                {data ? (
                    <span className="text-muted-foreground text-xs tabular-nums">
                        {data.total.toLocaleString()}{' '}
                        {data.total === 1 ? 'note' : 'notes'}
                    </span>
                ) : null}
            </header>
            <ScratchpadFilterBar
                search={search}
                availableTags={availableTags}
                searchDraft={searchDraft}
                onSearchDraftChange={setSearchDraft}
                onClearSearchDraft={clearSearchDraft}
                onPatch={patchSearch}
            />
            {showDegradedBanner ? <DegradedSearchBanner /> : null}
            <section className="bg-card/50 border-border/60 flex flex-col overflow-hidden rounded-xl border shadow-sm">
                {!selectionIsValid ? (
                    <EmptyState
                        icon={BookOpen}
                        title={
                            hasNoScopeKindForId
                                ? 'Choose a scope kind for this scope id'
                                : hasInvalidScopeId
                                  ? 'A global scope cannot use a scope id'
                                  : `Pick a scope ${
                                        promptForScopeKind === 'organization'
                                            ? 'organization'
                                            : promptForScopeKind === 'tool'
                                              ? 'tool'
                                              : promptForScopeKind === 'skill'
                                                ? 'skill'
                                                : 'kind'
                                    } to see notes`
                        }
                        description={
                            hasNoScopeKindForId
                                ? 'Choose one scope kind for this preserved id, or clear the id above.'
                                : hasInvalidScopeId
                                  ? 'Remove the scope id or choose one non-global scope kind before loading notes.'
                                  : 'This scope needs a specific target to avoid spanning every organization. Pick one above, toggle Cross-scope, or switch scope kind.'
                        }
                    />
                ) : isLoading ? (
                    <ListSkeleton />
                ) : hasError ? (
                    <EmptyState
                        icon={AlertTriangle}
                        title="Scratchpad failed to load."
                        description="Refresh the page. If it keeps failing, check the API service logs."
                    />
                ) : notes.length === 0 ? (
                    <EmptyState
                        icon={BookOpen}
                        title={
                            search.search
                                ? `No notes match “${search.search}”`
                                : 'No scratchpad notes for this scope yet'
                        }
                        description={
                            search.search
                                ? 'Clear the search or broaden the scope filters — agent writes only trigger moments.'
                                : 'The agent writes notes on trigger moments. Open a run and try `write_admin_note`.'
                        }
                        action={
                            search.search ? (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        clearSearchDraft();
                                        patchSearch({
                                            search: undefined,
                                            page: 1,
                                        });
                                    }}
                                >
                                    Clear search
                                </Button>
                            ) : null
                        }
                    />
                ) : (
                    notes.map((note, index) => (
                        <Fragment key={note.id}>
                            <StaggerWrapper delayIndex={index}>
                                <ScratchpadNoteRow
                                    note={note}
                                    linkSearch={search}
                                    highlighted={false}
                                />
                            </StaggerWrapper>
                        </Fragment>
                    ))
                )}
            </section>
            {data && data.total > DEFAULT_PER_PAGE ? (
                <IndexPagination
                    page={search.page}
                    totalPages={totalPages}
                    onPageChange={(nextPage) => {
                        patchSearch({ page: nextPage });
                    }}
                />
            ) : null}
        </div>
    );
}

function ListSkeleton() {
    return (
        <ul
            data-testid="scratchpad-list-skeleton"
            className="divide-border/40 divide-y"
        >
            {Array.from({ length: 6 }).map((_unused, index) => (
                <li key={index} className="flex flex-col gap-2 px-4 py-3">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-3 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                </li>
            ))}
        </ul>
    );
}

function DegradedSearchBanner() {
    return (
        <div
            role="status"
            className="flex items-center gap-2 rounded-md border border-amber-300/50 bg-amber-50/70 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-900/25 dark:text-amber-100"
        >
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            <span>
                Vector search is unavailable — falling back to Postgres
                full-text search. Ranking may be less precise.
            </span>
        </div>
    );
}

function StaggerWrapper({
    children,
    delayIndex,
}: {
    readonly children: React.ReactNode;
    readonly delayIndex: number;
}) {
    // A short, capped stagger — enough to feel deliberate on load, not
    // long enough to be perceived as slowness on a full page.
    const delayMs = Math.min(delayIndex * 18, 220);
    return (
        <div
            className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 duration-200"
            style={{ animationDelay: `${delayMs}ms` }}
        >
            {children}
        </div>
    );
}

function IndexPagination({
    page,
    totalPages,
    onPageChange,
}: {
    readonly page: number;
    readonly totalPages: number;
    readonly onPageChange: (page: number) => void;
}) {
    return (
        <nav
            className="text-muted-foreground flex items-center justify-between gap-3 text-xs"
            aria-label="Pagination"
        >
            <span className="tabular-nums">
                Page {page.toLocaleString()} of {totalPages.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
                <Button
                    variant="ghost"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => {
                        onPageChange(page - 1);
                    }}
                >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Previous
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => {
                        onPageChange(page + 1);
                    }}
                >
                    Next
                    <ChevronRight className="h-3.5 w-3.5" />
                </Button>
            </span>
        </nav>
    );
}
